import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    Typography, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { motion } from 'framer-motion';
import './QuestionFormModal.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';
import api from '../../../api';
import Swal from 'sweetalert2';

const questionTypes = [
    { label: 'Multiple Choice (Single Answer)', value: 'Single' },
    { label: 'Multiple Choice (Multiple Answers)', value: 'Multiple' },
    { label: 'True / False', value: 'TrueFalse' },
    // { label: 'Short Answer', value: 'Short' },
    // { label: 'Long Answer', value: 'Long' }
];

const defaultForm = {
    exam_id: '',
    subject_id: '',
    topic_id: '',
    question: '',
    questionType: '',
    a: '',
    b: '',
    c: '',
    d: '',
    correct: '',
    correctAnswers: [],
    answerText: '',
    questionImage: null,
    optionImages: {
        a: null,
        b: null,
        c: null,
        d: null
    }
};

const QuestionFormModal = ({ isOpen, onClose, onSubmit, editingQuestion }) => {
    const [formData, setFormData] = useState(defaultForm);

    const [questionImage, setQuestionImage] = useState(null);
    const [questionImagePreview, setQuestionImagePreview] = useState(null);

    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [isSubmitting, setIsSubmitting] = useState(false);


    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'questionType') {
            if (value === 'TrueFalse') {
                setFormData({
                    ...formData,
                    questionType: value,
                    a: 'True',
                    b: 'False',
                    c: '',
                    d: '',
                    correct: '',
                    correctAnswers: [],
                    answerText: '',
                    optionImages: { a: null, b: null, c: null, d: null }
                });
                return;
            }

            if (value === 'Single' || value === 'Multiple') {
                setFormData({
                    ...formData,
                    questionType: value,
                    a: '',
                    b: '',
                    c: '',
                    d: '',
                    correct: '',
                    correctAnswers: [],
                    answerText: '',
                    optionImages: { a: null, b: null, c: null, d: null }
                });
                return;
            }

            if (value === 'Short' || value === 'Long') {
                setFormData({
                    ...defaultForm,
                    questionType: value
                });
                return;
            }
        }


        if (name === 'exam_id') {
            setFormData(prev => ({
                ...prev,
                exam_id: value,
                subject_id: '', // reset subject
                topic_id: ''    // reset topic
            }));
            return;
        }

        if (name === 'subject_id') {
            setFormData(prev => ({
                ...prev,
                subject_id: value,
                topic_id: '' // reset topic
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    useEffect(() => {
        // Fetch all exams, subjects, and topics
        const fetchDropdownData = async () => {
            try {
                const [examRes, subjectRes, topicRes] = await Promise.all([
                    api.get('/exams'),
                    api.get('/subjects'),
                    api.get('/topics'),
                ]);

                console.log("Exam Data", examRes.data);
                console.log("Subject Data", subjectRes.data);
                console.log("Topic Data", topicRes.data);

                setExams(examRes.data);
                setSubjects(subjectRes.data);
                setTopics(topicRes.data);
            } catch (err) {
                console.error('Error fetching dropdown data:', err);
            }
        };

        if (isOpen) {
            fetchDropdownData();
        }
    }, [isOpen]);

    // Populate data when editing
    useEffect(() => {
        if (isOpen && typeof editingQuestion === 'object' && editingQuestion !== null) {
            console.log("Editing question received:", editingQuestion);
            setFormData({
                ...defaultForm,
                ...editingQuestion,
                correct: editingQuestion.correct_option?.toLowerCase() || '',
                correctAnswers: editingQuestion.correct_option_array?.map(opt => opt.toLowerCase()) || [],
                questionType:
                    editingQuestion.question_type === 'MCQ_RADIO' ? 'Single' :
                        editingQuestion.question_type === 'MCQ_CHECKBOX' ? 'Multiple' :
                            editingQuestion.question_type === 'true_false' ? 'TrueFalse' : '',
            });
        }
    }, [isOpen, editingQuestion]);


    const handleCheckboxChange = (option) => {
        setFormData((prev) => {
            const newAnswers = prev.correctAnswers.includes(option)
                ? prev.correctAnswers.filter((ans) => ans !== option)
                : [...prev.correctAnswers, option];
            console.log("Updated correctAnswers:", newAnswers); // 
            return { ...prev, correctAnswers: newAnswers };
        });
    };


    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setQuestionImage(file);
            setQuestionImagePreview(URL.createObjectURL(file));
        }
    };



    const handleQuestionImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                questionImage: file

            }));
        }
    };

    const handleOptionImageChange = (opt, e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                optionImages: {
                    ...prev.optionImages,
                    [opt]: file
                }
            }));
        }
    };




    const handleCloseIcon = () => {
        setFormData(defaultForm); // Reset form
        onClose(); // Close modals
    }


    const handleSubmit = async () => {
        if (!formData.exam_id || !formData.question || !formData.questionType) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill all required fields.'
            });
            return;
        }

        if ((formData.questionType === 'Single' || formData.questionType === 'TrueFalse') && !formData.correct) {
            Swal.fire({
                icon: 'warning',
                title: 'No Correct Answer',
                text: 'Please select a correct answer.'
            });
            return;
        }

        if (formData.questionType === 'Multiple' && formData.correctAnswers.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Correct Answers',
                text: 'Please select at least one correct answer.'
            });
            return;
        }

        let question_type = 'MCQ_RADIO';

        if (formData.questionType === 'Multiple') {
            question_type = 'MCQ_CHECKBOX';
        } else if (formData.questionType === 'Single') {
            question_type = 'MCQ_RADIO';
        } else if (formData.questionType === 'TrueFalse') {
            question_type = 'true_false';  // <== FIXED: lowercase
        } else if (formData.questionType === 'Short') {
            question_type = 'short';
        } else if (formData.questionType === 'Long') {
            question_type = 'long';
        }


        const payload = new FormData();

        payload.append('exam_id', formData.exam_id);
        if (formData.subject_id) payload.append('subject_id', formData.subject_id);
        if (formData.topic_id) payload.append('topic_id', formData.topic_id);
        console.log('Option A Image:', formData.option_a_image);
        console.log('Is File:', formData.option_a_image instanceof File);

        payload.append('question_text', formData.question);
        payload.append('question_type', question_type);
        payload.append('option_a', formData.a);
        payload.append('option_b', formData.b);
        payload.append('option_c', formData.c);
        payload.append('option_d', formData.d);
        payload.append('language_code', formData.languageCode || 'EN');
        payload.append('marks', formData.marks || 1);
        payload.append('status', 1);
        payload.append('is_delete', 0);



        if (formData.questionImage) {
            payload.append('question_image', formData.questionImage);
        }

        // Assuming your formData has this structure:
        const { optionImages } = formData;

        // Check and append each option image from the `optionImages` object
        if (optionImages?.a instanceof File) {
            payload.append('option_a_image', optionImages.a);
        }
        if (optionImages?.b instanceof File) {
            payload.append('option_b_image', optionImages.b);
        }
        if (optionImages?.c instanceof File) {
            payload.append('option_c_image', optionImages.c);
        }
        if (optionImages?.d instanceof File) {
            payload.append('option_d_image', optionImages.d);
        }




        // for (const opt of ['a', 'b', 'c', 'd']) {
        //     if (formData.optionImages[opt]) {
        //         payload.append(`option_image_${opt}`, formData.optionImages[opt]);
        //     }
        // }


        //replace this entire block with the below code
        // if (question_type === 'MCQ_CHECKBOX') {
        //     formData.correctAnswers.forEach(opt => {
        //         payload.append('correct_option_array[]', opt.toUpperCase());
        //     });
        // } else if (question_type === 'true_false') {
        //     const correct = formData.correct.toLowerCase();
        //     if (correct === 'true' || correct === 'false') {
        //         payload.append('correct_option', formData.correct.toUpperCase());
        //     }
        // } else {
        //     payload.append('correct_option', formData.correct.toUpperCase());
        // }


        if (question_type === 'MCQ_CHECKBOX') {
            if (Array.isArray(formData.correctAnswers) && formData.correctAnswers.length > 0) {
                const joined = formData.correctAnswers.map(opt => opt.toUpperCase()).join(',');
                payload.append('correct_option', joined);
            }
        } else if (question_type === 'true_false') {
            const correct = String(formData.correct).toLowerCase(); //  ensures it's a string
            if (correct === 'true' || correct === 'false') {
                payload.append('correct_option', correct.toUpperCase());
            } else {
                payload.append('correct_option', ''); // fallback
            }
        }
        else {
            // For radio and others
            payload.append('correct_option', formData.correct.toUpperCase());
        }



        try {
            setIsSubmitting(true); //  Add this line

            // Debug log: Show all form data before submission
            for (let pair of payload.entries()) {
                console.log(`${pair[0]}: ${pair[1]}`);
            }
            const response = await api.post('/questions', payload, {
                headers: {
                    'Content-Type': undefined
                }
            });


            console.log("Question submitted successfully:", response.data);
            onSubmit(response.data); // Callback to parent
            setFormData(defaultForm); // Reset form

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Question submitted successfully.'
            });

            onClose();
        } catch (error) {
            setIsSubmitting(false);
            if (error.response && error.response.data) {
                console.error("Validation errors:", error.response.data.errors);
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Submit',
                    html: `<pre>${JSON.stringify(error.response.data.errors, null, 2)}</pre>`,
                    customClass: {
                        popup: 'swal-wide'
                    }
                });
            } else {
                console.error("Unexpected error:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Unexpected Error',
                    text: 'Something went wrong. Please try again.'
                });
            }
        }
    };

    const renderOptions = () => {
        const { questionType } = formData;

        if (questionType === 'Short' || questionType === 'Long') return null;

        return (
            <>
                {['a', 'b', 'c', 'd'].map((opt) => {
                    if (questionType === 'TrueFalse' && (opt === 'c' || opt === 'd')) return null;

                    return (
                        <div key={opt}>
                            <TextField
                                fullWidth
                                label={`Option ${opt.toUpperCase()}`}
                                name={opt}
                                value={formData[opt]}
                                onChange={handleChange}
                                margin="dense"
                                disabled={questionType === 'TrueFalse'} // prevent editing True/False text
                            />

                            {/* Only show image upload if NOT TrueFalse */}
                            {questionType !== 'TrueFalse' && (
                                <>
                                    <Typography variant="caption">
                                        Upload image for Option {opt.toUpperCase()}
                                    </Typography>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleOptionImageChange(opt, e)}
                                        style={{ marginTop: 4 }}
                                    />
                                    {formData.optionImages[opt] && (
                                        <img
                                            src={formData.optionImages[opt]}
                                            label={`Option ${opt.toUpperCase()} Preview`}
                                            style={{ maxWidth: '100px', marginTop: 6 }}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    const renderAnswerFields = () => {
        const { questionType } = formData;

        switch (questionType) {
            case 'Single':
                return (
                    <FormControl fullWidth margin="dense">
                        <Typography>Select Correct Option</Typography>
                        <RadioGroup
                            value={formData.correct}
                            onChange={(e) => setFormData({ ...formData, correct: e.target.value })}
                        >
                            {['a', 'b', 'c', 'd'].map((opt) =>
                                formData[opt] ? (
                                    <FormControlLabel
                                        key={opt}
                                        value={opt}
                                        control={<Radio />}
                                        label={
                                            <span>
                                                {formData[opt]}
                                                {formData.optionImages[opt] && (
                                                    <img
                                                        src={formData.optionImages[opt]}
                                                        label={`Option ${opt.toUpperCase()}`}
                                                        style={{ maxWidth: '60px', marginLeft: 8, verticalAlign: 'middle' }}
                                                    />
                                                )}
                                            </span>
                                        }
                                    />
                                ) : null
                            )}
                        </RadioGroup>
                    </FormControl>
                );

            case 'Multiple':
                return (
                    <FormControl fullWidth margin="dense">
                        <Typography>Select Correct Options</Typography>
                        {['a', 'b', 'c', 'd'].map((opt) =>
                            formData[opt] ? (
                                <FormControlLabel
                                    key={opt}
                                    control={
                                        <Checkbox
                                            checked={formData.correctAnswers.includes(opt)}
                                            onChange={() => handleCheckboxChange(opt)}
                                        />
                                    }
                                    label={
                                        <span>
                                            {formData[opt]}
                                            {formData.optionImages[opt] && (
                                                <img
                                                    src={formData.optionImages[opt]}
                                                    label={`Option ${opt.toUpperCase()}`}
                                                    style={{ maxWidth: '60px', marginLeft: 8, verticalAlign: 'middle', height: 'auto' }}
                                                />
                                            )}
                                        </span>
                                    }
                                />
                            ) : null
                        )}
                    </FormControl>
                );

            case 'TrueFalse':
                return (
                    <FormControl fullWidth margin="dense">
                        <Typography>Select Correct Option</Typography>
                        <RadioGroup
                            value={formData.correct}
                            onChange={(e) => setFormData({ ...formData, correct: e.target.value })}
                        >
                            <FormControlLabel value="a" control={<Radio />} label="True" />
                            <FormControlLabel value="b" control={<Radio />} label="False" />
                        </RadioGroup>
                    </FormControl>
                );

            case 'Short':
            case 'Long':
                return (
                    <TextField
                        fullWidth
                        label="Expected Answer"
                        name="answerText"
                        value={formData.answerText}
                        onChange={handleChange}
                        margin="dense"
                        multiline
                        minRows={questionType === 'Long' ? 4 : 2}
                    />
                );

            default:
                return null;
        }
    };

    const filteredSubjects = formData.exam_id
        ? subjects.filter(sub => sub.exam_id === parseInt(formData.exam_id))
        : subjects;

    const filteredTopics = formData.subject_id
        ? topics.filter(topic => topic.subject_id === parseInt(formData.subject_id))
        : topics;


    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            {/* <DialogTitle>Add Question</DialogTitle> */}
            <DialogTitle
                sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        color: '#1976d2',
                        fontFamily: 'Roboto, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    📝 Add Question
                </motion.div>

                <IconButton
                    aria-label="close"
                    onClick={handleCloseIcon}
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'red', // 🔴 This makes the icon red
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                component={motion.div}
                sx={{ maxHeight: '70vh', overflowY: 'auto' }}
                initial={{ opacity: 0, y: -40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{
                        marginBottom: 16,
                        backgroundColor: '#e0f7fa',
                        border: '1px solid #00acc1',
                        color: '#006064',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <span role="img" aria-label="info">ℹ️</span>
                    <span>
                        If you enter text for a question <strong>and</strong> also upload an image, the text will be displayed <strong>above</strong> the image.
                    </span>
                </motion.div>


                <TextField
                    select
                    fullWidth
                    required
                    label="Select Exam"
                    name="exam_id"
                    value={formData.exam_id}
                    onChange={handleChange}
                    margin="dense"
                >
                    {exams.map((exam) => (
                        <MenuItem key={exam.id} value={exam.id}>
                            {exam.title}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    fullWidth
                    label="Select Subject (Optional)"
                    name="subject_id"
                    value={formData.subject_id}
                    onChange={handleChange}
                    margin="dense"
                >
                    <MenuItem value="">None</MenuItem>
                    {filteredSubjects.map((subject) => (
                        <MenuItem key={subject.id} value={subject.id}>
                            {subject.name}
                        </MenuItem>
                    ))}
                </TextField>



                <TextField
                    select
                    fullWidth
                    label="Select Topic (Optional)"
                    name="topic_id"
                    value={formData.topic_id}
                    onChange={handleChange}
                    margin="dense"
                >
                    <MenuItem value="">None</MenuItem>
                    {filteredTopics.map((topic) => (
                        <MenuItem key={topic.id} value={topic.id}>
                            {topic.title}
                        </MenuItem>
                    ))}
                </TextField>




                {/* textfield for entering question text */}
                <TextField
                    fullWidth
                    label="Question"
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    margin="dense"
                    multiline
                    minRows={2}
                />

                <Typography variant="body2" sx={{ mt: 1 }}>Upload Question Image (optional):</Typography>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleQuestionImageChange}
                />
                {formData.questionImage && (
                    <img
                        src={formData.questionImage}
                        alt="Question Preview"
                        style={{ maxWidth: '100%', maxHeight: '150px', marginTop: 8 }}
                    />
                )}

                <TextField
                    fullWidth
                    select
                    label="Question Type"
                    name="questionType"
                    value={formData.questionType}
                    onChange={handleChange}
                    margin="dense"
                >
                    {questionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                            {type.label}
                        </MenuItem>
                    ))}
                </TextField>

                {/* <select
                    className="form-select"
                    name="questionType"
                    value={formData.questionType}
                    onChange={handleChange}
                >
                    <option value="">Select Question Type</option>
                    <option value="MCQ_RADIO">Multiple Choice (Single Answer)</option>
                    <option value="MCQ_CHECKBOX">Multiple Choice (Multiple Answers)</option>
                    <option value="true_false">True/False</option>
                    <option value="short">Short Answer</option>
                    <option value="long">Long Answer</option>
                </select> */}


                {renderOptions()}
                {renderAnswerFields()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="error" variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained"
                    color="primary"
                    disabled={
                        !formData.exam_id || // make exam required
                        !formData.question ||
                        !formData.questionType ||
                        (formData.questionType === 'Single' && !formData.correct) ||
                        (formData.questionType === 'Multiple' && formData.correctAnswers.length === 0)
                    }
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuestionFormModal;